import { z } from 'zod';

/**
 * Tariff Schemas - Vortx Flux Rate Database
 *
 * Electric tariffs are one of two types:
 * 1. Blended rates (simple $/kWh + $/kW + fixed monthly)
 * 2. TOU rates (8760 hourly energy rates + monthly demand charges)
 *
 * All tariffs come from the Vortx Flux Rate Database (admin-approved).
 */

// Valid lengths for time-series data
const HOURLY_LENGTH = 8760;
const MONTHS_LENGTH = 12;

// TOU schedule grid: 12 months x 24 hours (for UI editing)
const TOUScheduleSchema = z.array(z.array(z.number().min(0)))
  .refine(
    (grid) => grid.length === MONTHS_LENGTH && grid.every(row => row.length === 24),
    { message: 'TOU schedule must be 12 months x 24 hours' }
  );

// 8760 hourly rates array (what gets sent to solver)
const HourlyRatesSchema = z.array(z.number().min(0))
  .refine(
    (arr) => arr.length === HOURLY_LENGTH,
    { message: `Energy rates must have exactly ${HOURLY_LENGTH} hourly values` }
  );

// Monthly values (12 months)
const MonthlyValuesSchema = z.array(z.number().min(0))
  .refine(
    (arr) => arr.length === MONTHS_LENGTH,
    { message: `Monthly values must have exactly ${MONTHS_LENGTH} values` }
  );

/**
 * TOU Demand Period - for complex seasonal/peak demand charges
 */
const TOUDemandPeriodSchema = z.object({
  name: z.string(),
  months: z.array(z.number().int().min(1).max(12)),
  hours: z.array(z.number().int().min(0).max(23)),
  rate_per_kw: z.number().min(0),
  days: z.enum(['weekday', 'weekend', 'all']).default('weekday'),
});

/**
 * Export Bin - for NEM 3.0 multi-tier export compensation
 */
const ExportBinSchema = z.object({
  name: z.string(),
  rate_per_kwh: z.number().min(0),
  max_kwh_per_year: z.number().min(0).optional(),
  time_varying_rates: HourlyRatesSchema.optional(),
});

/**
 * Blended Rate Tariff
 * Simple flat rates - good for quick estimates
 */
export const BlendedTariffSchema = z.object({
  type: z.literal('blended'),

  // Energy rate ($/kWh)
  blended_annual_energy_rate: z.number()
    .min(0, 'Energy rate cannot be negative')
    .max(2, 'Energy rate seems too high (>$2/kWh)'),

  // Demand rate ($/kW/month)
  blended_annual_demand_rate: z.number()
    .min(0, 'Demand rate cannot be negative')
    .default(0),

  // Fixed charges
  fixed_monthly_charge: z.number().min(0).default(0),
  min_monthly_charge: z.number().min(0).default(0),

  // Export/NEM rate ($/kWh credited for export)
  export_rate: z.number().min(0).default(0),

  // Metadata
  tariff_id: z.string().optional(),
  tariff_name: z.string().optional(),
  utility_name: z.string().optional(),
});

/**
 * TOU (Time-of-Use) Tariff
 * Full hourly energy rates + demand charges
 */
export const TOUTariffSchema = z.object({
  type: z.literal('tou'),

  // 8760 hourly energy rates ($/kWh per hour)
  energy_rates: HourlyRatesSchema,

  // Monthly demand rates ($/kW/month for each month)
  // Demand is measured in 15-min intervals, charged monthly
  monthly_demand_rates: MonthlyValuesSchema.optional(),

  // TOU demand periods (for seasonal/peak demand charges)
  tou_demand_periods: z.array(TOUDemandPeriodSchema).optional(),

  // Fixed charges
  fixed_monthly_charge: z.number().min(0).default(0),
  min_monthly_charge: z.number().min(0).default(0),

  // Export/NEM rates
  export_rate: z.number().min(0).default(0),
  export_bins: z.array(ExportBinSchema).optional(),

  // UI helper fields (12x24 grids for editing, converted to 8760)
  tou_energy_grid: TOUScheduleSchema.optional(),
  tou_demand_grid: TOUScheduleSchema.optional(),

  // Metadata
  tariff_id: z.string().optional(),
  tariff_name: z.string().optional(),
  utility_name: z.string().optional(),
});

/**
 * Main Electric Tariff Schema
 * Discriminated union of blended and TOU tariffs
 */
export const ElectricTariffSchema = z.discriminatedUnion('type', [
  BlendedTariffSchema,
  TOUTariffSchema,
]);

/**
 * Convert 12x24 TOU grid to 8760 hourly rates
 */
export const touGridTo8760 = (grid) => {
  if (!grid || grid.length !== 12) {
    throw new Error('TOU grid must have 12 months');
  }

  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const rates = [];

  for (let month = 0; month < 12; month++) {
    const days = daysInMonth[month];
    for (let day = 0; day < days; day++) {
      for (let hour = 0; hour < 24; hour++) {
        rates.push(grid[month][hour]);
      }
    }
  }

  return rates;
};

/**
 * Convert 8760 hourly rates to 12x24 TOU grid (for UI display)
 */
export const rates8760ToTouGrid = (rates) => {
  if (!rates || rates.length !== HOURLY_LENGTH) {
    throw new Error('Rates must have 8760 values');
  }

  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const grid = [];
  let idx = 0;

  for (let month = 0; month < 12; month++) {
    // For each month, average all days to get typical hourly pattern
    const hourlyTotals = Array(24).fill(0);
    const days = daysInMonth[month];

    for (let day = 0; day < days; day++) {
      for (let hour = 0; hour < 24; hour++) {
        hourlyTotals[hour] += rates[idx++];
      }
    }

    grid.push(hourlyTotals.map(total => total / days));
  }

  return grid;
};

/**
 * Normalize tariff from legacy or API formats to standard schema
 */
export const normalizeTariff = (tariff) => {
  if (!tariff) {
    throw new Error('Tariff is required');
  }

  // Already in new format
  if (tariff.type === 'blended' || tariff.type === 'tou') {
    return tariff;
  }

  // Convert from legacy format
  if (tariff.energy_rates && Array.isArray(tariff.energy_rates)) {
    return {
      type: 'tou',
      energy_rates: tariff.energy_rates,
      monthly_demand_rates: tariff.monthly_demand_rates,
      tou_demand_periods: tariff.tou_demand_periods,
      fixed_monthly_charge: tariff.fixedmonthlycharge || tariff.fixed_monthly_charge || 0,
      min_monthly_charge: tariff.minmonthlycharge || tariff.min_monthly_charge || 0,
      export_rate: tariff.export_rate || 0,
      export_bins: tariff.export_bins,
      tariff_id: tariff.tariff_id || tariff.id,
      tariff_name: tariff.tariff_name || tariff.name,
      utility_name: tariff.utility_name || tariff.utility,
    };
  }

  if (tariff.blended_annual_energy_rate !== undefined) {
    return {
      type: 'blended',
      blended_annual_energy_rate: tariff.blended_annual_energy_rate,
      blended_annual_demand_rate: tariff.blended_annual_demand_rate || 0,
      fixed_monthly_charge: tariff.fixedmonthlycharge || tariff.fixed_monthly_charge || 0,
      min_monthly_charge: tariff.minmonthlycharge || tariff.min_monthly_charge || 0,
      export_rate: tariff.export_rate || 0,
      tariff_id: tariff.tariff_id || tariff.id,
      tariff_name: tariff.tariff_name || tariff.name,
      utility_name: tariff.utility_name || tariff.utility,
    };
  }

  throw new Error('Unable to normalize tariff - unknown format');
};

/**
 * Validate tariff for common issues
 */
export const validateTariff = (tariff) => {
  const warnings = [];
  const errors = [];

  if (!tariff) {
    errors.push('Electric tariff is required');
    return { valid: false, errors, warnings };
  }

  let normalized;
  try {
    normalized = normalizeTariff(tariff);
  } catch (e) {
    errors.push(`Invalid tariff format: ${e.message}`);
    return { valid: false, errors, warnings };
  }

  if (normalized.type === 'blended') {
    if (!normalized.blended_annual_energy_rate || normalized.blended_annual_energy_rate <= 0) {
      errors.push('Blended energy rate must be greater than 0');
    }
    if (normalized.blended_annual_energy_rate > 1) {
      warnings.push('Energy rate is high (>$1/kWh) - verify this is correct');
    }
  }

  if (normalized.type === 'tou') {
    if (!normalized.energy_rates || normalized.energy_rates.length !== HOURLY_LENGTH) {
      errors.push(`TOU tariff must have ${HOURLY_LENGTH} hourly energy rates`);
    } else {
      const avgRate = normalized.energy_rates.reduce((a, b) => a + b, 0) / HOURLY_LENGTH;
      if (avgRate > 1) {
        warnings.push('Average energy rate is high (>$1/kWh) - verify this is correct');
      }
      if (avgRate <= 0) {
        errors.push('Average energy rate must be greater than 0');
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    normalized,
  };
};

/**
 * Gas Tariff Schema
 */
export const GasTariffSchema = z.object({
  rate_per_therm: z.number().min(0).optional(),
  rate_per_mmbtu: z.number().min(0).optional(),
  tiers: z.array(z.object({
    max_therms: z.number().optional(),
    rate: z.number().min(0),
  })).optional(),
  fixed_monthly_charge: z.number().min(0).default(0),
  tariff_id: z.string().optional(),
  tariff_name: z.string().optional(),
  utility_name: z.string().optional(),
}).optional();

export default ElectricTariffSchema;
